import { Descriptions, Card, Row, Col, Typography, Space, Avatar, Tag} from "antd";
import moment from "moment";
import { 
  FilePdfOutlined, 
  DownloadOutlined, 
  EyeOutlined, 
  IdcardOutlined, 
  HomeOutlined, 
  CreditCardOutlined, 
  UserOutlined, 
  TeamOutlined, 
  PhoneOutlined, 
  CalendarOutlined,
  BankOutlined,
} from "@ant-design/icons";
import CreditInformationSection from "./CreditInformationSection";

const { Title, Text } = Typography;

const RiderDetailsDrawer = ({ rider }) => {
  const fileBaseUrl = "https://applicationbackend.jalikoi.rw/";
  const getFullName = (person) => {
    if (!person) return "N/A";
    return `${person.first_name || ""} ${person.middle_name || ""} ${person.last_name || ""}`.trim();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      APPROVED: { color: "green", text: "Approved" },
      PENDING: { color: "orange", text: "Pending" },
      REJECTED: { color: "red", text: "Rejected" },
      REVIEW: { color: "blue", text: "Under Review" },
      INGARAGU: { color: "purple", text: "Ingaragu" },
    };
    
    const statusInfo = statusMap[status] || { color: "default", text: status || "N/A" };
    return <Tag className="text-white" color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  

  const renderFileSection = (assignType) => {
    const files = rider?.files?.filter((file) => file.assign === assignType);
    return files && files.length > 0 ? (
      files.map((file, index) => (
        <div key={index} className="flex flex-col items-center">
          <Avatar
            size={120}
            src={`${fileBaseUrl}${file.path}`}
            alt={getFullName(rider)}
            className="shadow-md border-4 border-white"
          />
        </div>
      ))
    ) : (
      <div className="flex items-center justify-center bg-white rounded-full h-32 w-32 shadow-md border-4 border-white">
        <UserOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />
      </div>
    );
  };

  const SectionCard = ({ title, icon, children }) => (
    <Card 
      className="mb-6 shadow-sm" 
      title={
        <Space>
          {icon}
          <span>{title}</span>
        </Space>
      }
      bordered={true}
    >
      {children}
    </Card>
  );

  // Custom badge with white text
  const WhiteTextBadge = ({ color, text }) => (
    <span style={{ 
      backgroundColor: color, 
      color: 'white', 
      padding: '2px 8px', 
      borderRadius: '10px', 
      fontSize: '12px',
      fontWeight: '500'
    }}>
      {text}
    </span>
  );



  return (
    <div className="bg-gray-50 rounded-lg">
      {/* Header with Blue Background */}
      <div style={{ background: "#23419B", borderRadius: "8px 8px 0 0", padding: "20px", marginBottom: "24px" }}>
        <Row gutter={24} align="middle">
          <Col xs={24} md={6} className="text-center">
            <div className="flex justify-center">
              {renderFileSection("PHOTO")}
            </div>
          </Col>
          <Col xs={24} md={18}>
            <Title level={3} style={{ margin: 0, color: "white" }}>{getFullName(rider)}</Title>
            <Space className="mt-2" size="large" style={{ color: "rgba(255, 255, 255, 0.85)" }}>
              <Space>
                <IdcardOutlined style={{ color: "rgba(255, 255, 255, 0.85)" }} />
                <Text style={{ color: "rgba(255, 255, 255, 0.85)" }}>{rider?.nid || "N/A"}</Text>
              </Space>
              <Space>
                <PhoneOutlined style={{ color: "rgba(255, 255, 255, 0.85)" }} />
                <Text style={{ color: "rgba(255, 255, 255, 0.85)" }}>{rider?.phone || "N/A"}</Text>
              </Space>
            </Space>
            <div className="mt-3">
              {getStatusBadge(rider?.status)}
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 pb-4">
        <Row gutter={[24, 24]}>
          {/* Personal Info */}
          <Col xs={24} lg={12}>
            <SectionCard title="Personal Information" icon={<IdcardOutlined />}>
              <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: "500" }}>
                <Descriptions.Item label="Full Name">{getFullName(rider)}</Descriptions.Item>
                <Descriptions.Item label="Date of Birth">
                  <Space>
                    <CalendarOutlined />
                    {rider?.dob ? moment(rider.dob).format("Do MMMM YYYY") : "N/A"}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="National ID">{rider?.nid || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Phone">{rider?.phone || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Gender">{rider?.gender || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Driving License">{rider?.driving_licency || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="License Issued">
                  {rider?.driving_licency_issued_date ? 
                    moment(rider.driving_licency_issued_date).format("DD/MM/YYYY") : "N/A"}
                </Descriptions.Item>
              </Descriptions>
            </SectionCard>
          </Col>

          {/* Moto Leasing */}
          <Col xs={24} lg={12}>
            <SectionCard title="Moto Details" icon={<CreditCardOutlined />}>
              <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: "500" }}>
                <Descriptions.Item label="Moto Type">
                  <Tag color="blue">{rider?.moto_leasing?.type || "N/A"}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Ownership">{rider?.moto_ownership || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Experience">{rider?.moto_experience || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Cooperative">{rider?.cooperative || "N/A"}</Descriptions.Item>
              </Descriptions>
            </SectionCard>
          </Col>

          

          {/* Place of Birth */}
          <Col xs={24} lg={12}>
            <SectionCard title="Place of Birth" icon={<HomeOutlined />}>
              <div className="p-4 bg-gray-50 rounded mb-3">
                <Row gutter={[8, 8]}>
                  <Col span={24}><Text strong>Village:</Text> {rider?.place_of_birth?.name || "N/A"}</Col>
                  <Col span={24}><Text strong>Cell:</Text> {rider?.place_of_birth?.cells?.name || "N/A"}</Col>
                  <Col span={24}><Text strong>Sector:</Text> {rider?.place_of_birth?.cells?.sector?.name || "N/A"}</Col>
                  <Col span={24}><Text strong>District:</Text> {rider?.place_of_birth?.cells?.sector?.district?.name || "N/A"}</Col>
                  <Col span={24}><Text strong>Province:</Text> {rider?.place_of_birth?.cells?.sector?.district?.province?.name || "N/A"}</Col>
                </Row>
              </div>
            </SectionCard>
          </Col>

          {/* Residence */}
          <Col xs={24} lg={12}>
            <SectionCard title="Current Residence" icon={<HomeOutlined />}>
              <div className="p-4 bg-gray-50 rounded mb-3">
                <Row gutter={[8, 8]}>
                  <Col span={24}><Text strong>Village:</Text> {rider?.residence?.village?.name || "N/A"}</Col>
                  <Col span={24}><Text strong>Cell:</Text> {rider?.residence?.village?.cells?.name || "N/A"}</Col>
                  <Col span={24}><Text strong>Sector:</Text> {rider?.residence?.village?.cells?.sector?.name || "N/A"}</Col>
                  <Col span={24}><Text strong>District:</Text> {rider?.residence?.village?.cells?.sector?.district?.name || "N/A"}</Col>
                  <Col span={24}><Text strong>Province:</Text> {rider?.residence?.village?.cells?.sector?.district?.province?.name || "N/A"}</Col>
                </Row>
              </div>
            </SectionCard>
          </Col>
          
          {/* Financial Info */}
          <Col xs={24}>
            <SectionCard 
              title="Financial Details" 
              icon={<CreditCardOutlined />}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Card type="inner" title="Income & Payments" className="h-full">
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary">Daily Income</Text>
                        <div className="text-lg font-medium">{rider?.daily_income ? `${rider.daily_income} RWF` : "N/A"}</div>
                      </div>
                      <div>
                        <Text type="secondary">Loan Payment Process</Text>
                        <div className="text-lg font-medium">{rider?.loan_payment_process || "N/A"}</div>
                      </div>
                      <div>
                        <Text type="secondary">Has Debts</Text>
                        <div className="text-lg font-medium">
                          {rider?.debts ? 
                            <Tag color="orange">Yes</Tag> : 
                            <Tag color="green">No</Tag>
                          }
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card type="inner" title="Housing" className="h-full">
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary">Monthly Rent</Text>
                        <div className="text-lg font-medium">{rider?.monthly_rent ? `${rider.monthly_rent} RWF` : "N/A"}</div>
                      </div>
                      <div>
                        <Text type="secondary">Rent Duration</Text>
                        <div className="text-lg font-medium">{rider?.rent_duration || "N/A"}</div>
                      </div>
                      <div>
                        <Text type="secondary">Home Ownership</Text>
                        <div className="text-lg font-medium">
                          {rider?.home_ownership ? 
                            <Tag color="green">Yes</Tag> : 
                            <Tag color="orange">No</Tag>
                          }
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>
              </Row>
            </SectionCard>
          </Col>

          {/* Contacts */}
          <Col xs={24}>
            <SectionCard title="Related Contacts" icon={<TeamOutlined />}>
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Card 
                    type="inner" 
                    title={
                      <Space>
                        <UserOutlined />
                        <span>Primary Insurer</span>
                      </Space>
                    }
                    className="h-full"
                    extra={<WhiteTextBadge color="green" text="Primary" />}
                  >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary">Name</Text>
                        <div className="text-lg font-medium">{getFullName(rider.insurer_one)}</div>
                      </div>
                      <Row gutter={24}>
                        <Col span={12}>
                          <Text type="secondary">ID</Text>
                          <div>{rider?.insurer_one?.identity || "N/A"}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">Phone</Text>
                          <div>{rider?.insurer_one?.phone || "N/A"}</div>
                        </Col>
                      </Row>
                      <div>
                        <Text type="secondary">Occupation</Text>
                        <div>{rider?.insurer_one?.occupation || "N/A"}</div>
                      </div>
                    </Space>
                  </Card>
                </Col>
                
                {rider?.partner && (
                  <Col xs={24} md={12}>
                    <Card 
                      type="inner" 
                      title={
                        <Space>
                          <UserOutlined />
                          <span>Rider Partner</span>
                        </Space>
                      }
                      className="h-full"
                    >
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                          <Text type="secondary">Name</Text>
                          <div className="text-lg font-medium">{getFullName(rider.partner)}</div>
                        </div>
                        <Row gutter={24}>
                          <Col span={12}>
                            <Text type="secondary">ID</Text>
                            <div>{rider?.partner?.nid || "N/A"}</div>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary">Phone</Text>
                            <div>{rider?.partner?.phone || "N/A"}</div>
                          </Col>
                        </Row>
                        <div>
                          <Text type="secondary">Occupation</Text>
                          <div>{rider?.partner?.occupation || "N/A"}</div>
                        </div>
                      </Space>
                    </Card>
                  </Col>
                )}
              </Row>
            </SectionCard>
          </Col>

          {/* Files */}
          <Col span={24}>
            <SectionCard title="Uploaded Documents" icon={<FilePdfOutlined />}>
              {rider?.files && rider.files.length > 0 ? (
                <Row gutter={[16, 16]}>
                  {rider.files.map((file, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <Card 
                        hoverable
                        size="small" 
                        className="text-center"
                        cover={
                          <div style={{ background: '#f0f2f5', padding: '20px', display: 'flex', justifyContent: 'center' }}>
                            <FilePdfOutlined style={{ fontSize: 40, color: "#23419B" }} />
                          </div>
                        }
                        actions={[
                          <a href={`${fileBaseUrl}${file.path}`} target="_blank" rel="noopener noreferrer">
                            <EyeOutlined key="view" /> View
                          </a>,
                          <a href={`${fileBaseUrl}${file.path}`} target="_blank" download rel="noopener noreferrer">
                            <DownloadOutlined key="download" /> Download
                          </a>
                        ]}
                      >
                        <Card.Meta 
                          title={file.assign.replace("_", " ")} 
                          description={
                            <Tag color="#23419B" style={{ margin: '8px 0 0 0', color: 'white' }}>
                              Document
                            </Tag>
                          }
                        />
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-center bg-gray-50 rounded p-6">
                  <Space direction="vertical" align="center">
                    <FilePdfOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />
                    <Text className="text-gray-500">No documents uploaded</Text>
                  </Space>
                </div>
              )}
            </SectionCard>
          </Col>
          {/* Credit Information Section */}
          <Col xs={24}>
            <SectionCard title="Credit Information (CRB)" icon={<BankOutlined />}>
              <CreditInformationSection  rider={rider}  />
            </SectionCard>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default RiderDetailsDrawer;